// src/components/PersonalInfo.js
import React from 'react';
import { FaUserCircle, FaCamera } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import countries from './countries';

const PersonalInfo = ({ formData, handleChange, handleFileChange, errors, preview, setPreview }) => {
  console.log('📝 PersonalInfo rendered with formData:', formData);
  console.log('❌ PersonalInfo errors:', errors);

  const getBorderClass = (field) => {
    const hasError = errors[field];
    console.log(`🔍 Field "${field}" has error:`, hasError, 'value:', formData[field]);
    return hasError ? 'is-invalid' : '';
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image size must be less than 2MB.");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
    handleFileChange('profilePicture', file);
    toast.success("Profile picture selected!");
  };

  const fadeIn = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5 } };

  return (
    <motion.div {...fadeIn}>
      {/* Profile Picture Upload */}
      <div className="d-flex flex-column align-items-center mb-4">
        <div className="position-relative">
          {preview ? (
            <img
              src={preview}
              alt="Profile Preview"
              className="rounded-circle object-fit-cover border border-3 border-white shadow"
              style={{ width: '112px', height: '112px' }}
            />
          ) : (
            <FaUserCircle className="text-secondary" style={{ fontSize: '112px' }} />
          )}
          <motion.div
            className="position-absolute"
            style={{ bottom: '-4px', right: '-4px' }}
            title="Upload a profile picture"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <label
              htmlFor="profilePicture"
              className="btn btn-sm rounded-circle d-flex align-items-center justify-center text-white"
              style={{
                backgroundColor: '#6B8E23',
                width: '32px',
                height: '32px',
                cursor: 'pointer'
              }}
            >
              <FaCamera />
              <input
                type="file"
                id="profilePicture"
                name="profilePicture"
                accept="image/png, image/jpeg, image/jpg"
                onChange={handleImageUpload}
                className="visually-hidden"
              />
            </label>
          </motion.div>
        </div>
        {errors.profilePicture && <div className="invalid-feedback d-block">{errors.profilePicture}</div>}
      </div>

      {/* Section Header */}
      <div className="text-center mb-4">
        <h2 className="h3 fw-semibold" style={{ color: '#6B8E23' }}>Personal Information</h2>
        <p className="small text-danger">Fields marked with <span className='text-danger'>*</span> are required.</p>
      </div>

      {/* Name Fields */}
      <div className="card border-0 shadow-sm mb-4" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="card-body p-4">
          <div className="row g-3">
            <div className="col-md-4">
              <label htmlFor="title" className="form-label fw-medium">
                Title <span className="text-danger">*</span>
              </label>
              <select
                id="title"
                name="title"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                className={`form-select ${getBorderClass('title')}`}
              >
                <option value="">Select</option>
                <option>Mr.</option>
                <option>Mrs.</option>
                <option>Ms.</option>
                <option>Dr.</option>
                <option>Prof.</option>
                <option>RRT.</option>
                <option>RCP.</option>
                <option>RT.</option>
                <option>RN.</option>
                <option>CRNA</option>
                <option>PT/DPT</option>
                <option>OT/OTD</option>
                <option>SLP</option>
                <option>PA/PA-C</option>
                <option>NP/APRN</option>
                <option>DDS/DMD</option>
                <option>DVM</option>
                <option>PharmD</option>
                <option>OD</option>
                <option>LCSW</option>
                <option>LPN</option>
                <option>LMT</option>
                <option>RD/RDN</option>
                <option>DO</option>
              </select>
              {errors.title && <div className="invalid-feedback d-block">{errors.title}</div>}
            </div>
            <div className="col-md-4">
              <label htmlFor="firstName" className="form-label fw-medium">
                First Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                className={`form-control ${getBorderClass('firstName')}`}
                placeholder="e.g., John"
              />
              {errors.firstName && <div className="invalid-feedback d-block">{errors.firstName}</div>}
            </div>
            <div className="col-md-4">
              <label htmlFor="lastName" className="form-label fw-medium">
                Last Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                className={`form-control ${getBorderClass('lastName')}`}
                placeholder="e.g., Jackson"
              />
              {errors.lastName && <div className="invalid-feedback d-block">{errors.lastName}</div>}
            </div>
          </div>

          <div className="row g-3 mt-2">
            <div className="col-md-6">
              <label htmlFor="email" className="form-label fw-medium">
                Email Address <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className={`form-control ${getBorderClass('email')}`}
                placeholder="you@example.com"
              />
              {errors.email && <div className="invalid-feedback d-block">{errors.email}</div>}
            </div>
            <div className="col-md-6">
              <label htmlFor="phone" className="form-label fw-medium">
                Phone Number <span className="text-danger">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={(e) => {
                  // Only allow digits, +, -, spaces, and parentheses
                  const sanitized = e.target.value.replace(/[^0-9+\-\s()]/g, '');
                  handleChange('phone', sanitized);
                }}
                maxLength={20}
                className={`form-control ${getBorderClass('phone')}`}
                placeholder="+233 24 123 4567"
                pattern="[0-9+\-\s()]{7,20}"
              />
              <small className="text-muted">Include country code (e.g., +233)</small>
              {errors.phone && <div className="invalid-feedback d-block">{errors.phone}</div>}
            </div>
          </div>

          <div className="row g-3 mt-2">
            <div className="col-md-6">
              <label htmlFor="dateOfBirth" className="form-label fw-medium">
                Date of Birth <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                id="dateOfBirth"
                name="dateOfBirth"
                value={formData.dateOfBirth || ''}
                onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                className={`form-control ${getBorderClass('dateOfBirth')}`}
                max={new Date().toISOString().split('T')[0]}
              />
              {errors.dateOfBirth && <div className="invalid-feedback d-block">{errors.dateOfBirth}</div>}
            </div>
            <div className="col-md-6">
              <label htmlFor="gender" className="form-label fw-medium">
                Gender <span className="text-danger">*</span>
              </label>
              <select
                id="gender"
                name="gender"
                value={formData.gender || ''}
                onChange={(e) => handleChange('gender', e.target.value)}
                className={`form-select ${getBorderClass('gender')}`}
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
              {errors.gender && <div className="invalid-feedback d-block">{errors.gender}</div>}
            </div>
          </div>
        </div>
      </div>

      {/* Mailing Address */}
      <div className="card border-0 shadow-sm" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="card-body p-4">
          <h3 className="h5 fw-medium mb-3" style={{ color: '#6B8E23' }}>Mailing Address</h3>
          <div className="row g-3">
            <div className="col-12">
              <label htmlFor="streetAddress" className="form-label fw-medium">
                Street Address <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="streetAddress"
                name="streetAddress"
                value={formData.streetAddress}
                onChange={(e) => handleChange('streetAddress', e.target.value)}
                className={`form-control ${getBorderClass('streetAddress')}`}
                placeholder="123 Nelson Mandela Avenue"
              />
              {errors.streetAddress && <div className="invalid-feedback d-block">{errors.streetAddress}</div>}
            </div>
            <div className="col-md-4">
              <label htmlFor="city" className="form-label fw-medium">
                City <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
                className={`form-control ${getBorderClass('city')}`}
                placeholder="Cape Town"
              />
              {errors.city && <div className="invalid-feedback d-block">{errors.city}</div>}
            </div>
            <div className="col-md-4">
              <label htmlFor="stateProvince" className="form-label fw-medium">
                State / Province <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="stateProvince"
                name="stateProvince"
                value={formData.stateProvince}
                onChange={(e) => handleChange('stateProvince', e.target.value)}
                className={`form-control ${getBorderClass('stateProvince')}`}
                placeholder="Western Cape"
              />
              {errors.stateProvince && <div className="invalid-feedback d-block">{errors.stateProvince}</div>}
            </div>
            <div className="col-md-4">
              <label htmlFor="postalCode" className="form-label fw-medium">
                Postal / Zip Code <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                id="postalCode"
                name="postalCode"
                value={formData.postalCode}
                onChange={(e) => handleChange('postalCode', e.target.value)}
                className={`form-control ${getBorderClass('postalCode')}`}
                placeholder="8001"
              />
              {errors.postalCode && <div className="invalid-feedback d-block">{errors.postalCode}</div>}
            </div>
            <div className="col-12">
              <label htmlFor="country" className="form-label fw-medium">
                Country <span className="text-danger">*</span>
              </label>
              <select
                id="country"
                name="country"
                value={formData.country}
                onChange={(e) => handleChange('country', e.target.value)}
                className={`form-select ${getBorderClass('country')}`}
              >
                <option value="">Select a country</option>
                {countries.map(country => <option key={country.code} value={country.name}>{country.name}</option>)}
              </select>
              {errors.country && <div className="invalid-feedback d-block">{errors.country}</div>}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PersonalInfo;